import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

function parseBackendDate(value: unknown): Date | null {
  if (value === null || value === undefined || value === '') return null;

  if (typeof value === 'number' && Number.isFinite(value)) {
    const ms = value > 1e12 ? value : value > 1e9 ? value * 1000 : value;
    const date = new Date(ms);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const raw = String(value).trim();
  if (!raw) return null;

  if (/^\d{10}$/.test(raw)) {
    const date = new Date(Number(raw) * 1000);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (/^\d{13}$/.test(raw)) {
    const date = new Date(Number(raw));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const date = new Date(raw.includes('T') ? raw : raw.replace(' ', 'T'));
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatBackendDate(value: unknown, fallback = '—'): string {
  const date = parseBackendDate(value);
  if (!date) {
    if (value === null || value === undefined || value === '') return fallback;
    return typeof value === 'string' ? value : fallback;
  }

  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/** Örn. `2026-03-23 10:31:47` — tarih + saat (tr-TR) */
export function formatBackendDateTime(value: unknown, fallback = '—'): string {
  const date = parseBackendDate(value);
  if (!date) {
    if (value === null || value === undefined || value === '') return fallback;
    const s = String(value).trim();
    return s || fallback;
  }

  return date.toLocaleString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export function normalizeTurkishPhoneNumber(value: string): string {
  let digits = value.replace(/\D/g, '');

  if (digits.startsWith('90') && digits.length > 10) {
    digits = digits.slice(2);
  }

  if (digits.startsWith('0') && digits.length > 10) {
    digits = digits.slice(1);
  }

  return digits.slice(0, 10);
}

export function isValidTurkishPhoneNumber(value: string): boolean {
  return /^5\d{9}$/.test(normalizeTurkishPhoneNumber(value));
}

/**
 * Query string üretir; `URLSearchParams.toString()` aksine boşlukları `+` değil `%20` yazar
 * (örn. `search=Arif%20Dikkanat`). Çok kelimeli aramalarda backend’e `+` gitmez.
 */
export function serializeQueryParams(
  params: Record<string, unknown>,
  allowEmptyValueKeys: readonly string[] = ['search', 's'],
): string {
  const allowEmpty = new Set(allowEmptyValueKeys);
  const pairs: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (
      value === undefined ||
      value === null ||
      (value === '' && !allowEmpty.has(key))
    ) {
      continue;
    }
    pairs.push(
      `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`,
    );
  }
  return pairs.join('&');
}

/** `URLSearchParams` / `ReadonlyURLSearchParams` → aynı `%20` kurallı query string */
export function serializeQueryParamsFromUrlSearchParams(
  params: Pick<URLSearchParams, 'forEach'>,
): string {
  const pairs: string[] = [];
  params.forEach((value, key) => {
    pairs.push(
      `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
    );
  });
  return pairs.join('&');
}

/** İşveren vergi levhası / imza sirküleri `*_status` alanı onaylı mı? (TR/EN veya "1") */
export function isCompanyCertificateApprovedStatus(
  status: string | null | undefined,
): boolean {
  if (status == null || status === '') return false;
  const s = String(status).trim().toLowerCase();
  if (s === '1' || s === 'true') return true;
  if (s === 'approved') return true;
  // "Onay Bekliyor" içinde "onayland" yok; "Onaylandı" / "onaylandi" yakalanır
  if (s.includes('onayland')) return true;
  return false;
}

/** İşveren vergi levhası / imza sirküleri `*_status` alanı reddedilmiş mi? (TR/EN veya "2") */
export function isCompanyCertificateRejectedStatus(
  status: string | null | undefined,
): boolean {
  if (status == null || status === '') return false;
  const s = String(status).trim().toLowerCase();
  if (s === '2') return true;
  if (s === 'rejected') return true;
  // "Reddedildi" / "reddedildi"
  if (s.includes('reddedil')) return true;
  return false;
}

/** Admin panel tarih filtreleri — Europe/Istanbul (+03:00, yaz saati yok) */
const ADMIN_DATE_TZ = '+03:00';

export function parseDateInputValue(
  dateValue: string,
): { y: number; m: number; d: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateValue.trim());
  if (!match) return null;
  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  if (!Number.isFinite(y) || m < 1 || m > 12 || d < 1 || d > 31) return null;
  return { y, m, d };
}

/** `<input type="date">` → backend `date_from` (ms, seçilen gün 00:00 TR) */
export function dateInputToStartMs(dateValue: string): number | undefined {
  const p = parseDateInputValue(dateValue);
  if (!p) return undefined;
  const iso = `${p.y}-${String(p.m).padStart(2, '0')}-${String(p.d).padStart(2, '0')}T00:00:00.000${ADMIN_DATE_TZ}`;
  const ms = new Date(iso).getTime();
  return Number.isNaN(ms) ? undefined : ms;
}

/** `<input type="date">` → backend `date_to` (ms, seçilen gün 23:59:59.999 TR) */
export function dateInputToEndMs(dateValue: string): number | undefined {
  const p = parseDateInputValue(dateValue);
  if (!p) return undefined;
  const iso = `${p.y}-${String(p.m).padStart(2, '0')}-${String(p.d).padStart(2, '0')}T23:59:59.999${ADMIN_DATE_TZ}`;
  const ms = new Date(iso).getTime();
  return Number.isNaN(ms) ? undefined : ms;
}

export function slugify(text: string): string {
  const trMap: Record<string, string> = {
    ç: 'c', Ç: 'c',
    ğ: 'g', Ğ: 'g',
    ı: 'i', I: 'i', İ: 'i',
    ö: 'o', Ö: 'o',
    ş: 's', Ş: 's',
    ü: 'u', Ü: 'u',
  };

  return text
    .split('')
    .map((char) => trMap[char] || char)
    .join('')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

